/**
 * Updates the recently viewed products in localStorage.
 *
 * MAX_PRODUCTS raised 4 -> 12 (2026-09-24) so the homepage recently-viewed
 * rail (assets/pag-recently-viewed.js) has enough history to fill a 4-card
 * row; predictive search's own strip stays capped at 4 separately, in
 * assets/predictive-search.js.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products in session storage */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 12;
  /** @static @constant {string} Event dispatched on document whenever the list changes, so
   *  every surface showing it (not just the one that changed it) can re-render. */
  static CHANGE_EVENT = 'pag:recently-viewed:change';

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productId - The ID of the product to add.
   */
  static addProduct(productId) {
    let viewedProducts = this.getProducts();

    viewedProducts = viewedProducts.filter((/** @type {string} */ id) => id !== productId);
    viewedProducts.unshift(productId);
    viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);

    try {
      localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));
    } catch {
      return;
    }
    this.#announce();
  }

  static clearProducts() {
    try {
      localStorage.removeItem(this.#STORAGE_KEY);
    } catch {
      return;
    }
    this.#announce();
  }

  static #announce() {
    document.dispatchEvent(new CustomEvent(RecentlyViewed.CHANGE_EVENT));
  }

  /**
   * Retrieves the list of recently viewed products from session storage.
   * @returns {string[]} The list of viewed products.
   */
  static getProducts() {
    try {
      return JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
    } catch {
      return [];
    }
  }
}
